// src/components/charts/DistrictProblemsChart.js
import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const DistrictProblemsChart = ({ allData, districtData }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!districtData || !districtData.length || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Автоматическая настройка высоты в зависимости от количества районов
    const districtsCount = districtData.length;
    const baseHeight = 400; // Базовая высота
    const rowHeight = 25; // Высота на один район
    const calculatedHeight = Math.max(baseHeight, districtsCount * rowHeight);

    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: districtData.map(d => d.district),
        datasets: [{
          label: 'Процент ОУ с недочетами',
          data: districtData.map(d => d.problemPercentage),
          backgroundColor: districtData.map(d => 
            d.problemPercentage > 50 ? 'rgba(231, 76, 60, 0.7)' : 
            d.problemPercentage > 25 ? 'rgba(243, 156, 18, 0.7)' : 'rgba(52, 152, 219, 0.7)'
          ),
          borderColor: districtData.map(d => 
            d.problemPercentage > 50 ? 'rgba(231, 76, 60, 1)' : 
            d.problemPercentage > 25 ? 'rgba(243, 156, 18, 1)' : 'rgba(52, 152, 219, 1)'
          ),
          borderWidth: 1,
          barThickness: 'flex',
          maxBarThickness: 20,
          minBarLength: 5
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        // Отключаем все анимации
        animation: {
          duration: 0 // Отключаем анимацию
        },
        transitions: {
          active: {
            animation: {
              duration: 0
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: 'Процент ОУ с недочетами (%)'
            },
            ticks: {
              callback: function(value) {
                return value + '%';
              },
              maxTicksLimit: 10
            },
            grid: {
              display: true
            }
          },
          y: {
            ticks: {
              autoSkip: false,
              font: {
                size: 11
              },
              callback: function(value, index) {
                // Сокращаем длинные названия районов
                const label = this.getLabelForValue(index);
                if (label.length > 30) {
                  return label.substring(0, 30) + '...';
                }
                return label;
              }
            },
            afterFit: function(axis) {
              axis.width = 180; // Фиксированная ширина для оси Y
            },
            grid: {
              display: false
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'Процент ОУ с недочетами по районам',
            font: {
              size: 14
            },
            padding: {
              bottom: 20
            }
          },
          tooltip: {
            // Отключаем анимацию тултипов
            animation: {
              duration: 0
            },
            callbacks: {
              title: function(context) {
                // В тултипе показываем полное название
                return districtData[context[0].dataIndex].district;
              },
              label: function(context) {
                return `Процент ОУ с недочетами: ${context.parsed.x.toFixed(1)}%`;
              },
              afterLabel: function(context) {
                const data = districtData[context.dataIndex];
                return `ОУ с недочетами: ${data.problemSchools} из ${data.totalSchools}`;
              }
            }
          },
          legend: {
            display: false
          }
        },
        layout: {
          padding: {
            left: 5,
            right: 15,
            top: 10,
            bottom: 10
          }
        },
        // Отключаем анимации при взаимодействии
        hover: {
          animation: {
            duration: 0
          }
        },
        responsiveAnimationDuration: 0
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [districtData]);

  // Автоматический расчет высоты контейнера
  const containerHeight = Math.max(400, (districtData?.length || 0) * 25);

  if (!districtData || !districtData.length) {
    return (
      <div style={{ 
        position: 'relative', 
        height: '400px',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="no-data">Нет данных для отображения</div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative', 
      height: `${containerHeight}px`,
      width: '100%',
      overflow: 'hidden'
    }}>
      <canvas ref={chartRef} id="district-problems-chart" />
    </div>
  );
};

export default DistrictProblemsChart;